/* eslint-disable react/destructuring-assignment */
// @flow
import * as React from 'react';
import '../leaflet_';
import {
    FeatureGroup, Map, Pane, Polygon, TileLayer,
} from 'react-leaflet';
import {
    defineMessages, FormattedMessage, injectIntl, type IntlShape,
} from 'react-intl';
import type { LatLng } from 'react-leaflet/lib/types';
import {
    typeof ApiClient, Confirmation, connectAPI, withConfirmation, withRouter,
} from 'speed-js-react';
import MessageDialog from 'esm-web-pro/components/dialogs/MessageDialog';
import { get, isEqual, throttle } from 'lodash';
import Spinner from 'esm-web-pro/components/forms/Spinner';
import DrawControl from 'esm-web-pro/components/maps/DrawControl';
import SidePaneMapLayerContainer from 'esm-web-pro/containers/pages/SidePaneMapLayerContainer';
import { Route, History, Match } from 'react-router-dom';
import MapObjectSidePanel from 'esm-web-pro/containers/map/MapObject';
import AuditEventTimeline from 'esm-web-pro/containers/AuditEventTimeline';
import CreatorContainer from 'esm-web-pro/containers/map/CreatorContainer';
import { MapContext } from 'esm-web-pro/containers/map/context';
import sidePanelMessages from 'esm-web-pro/messages/sidePanelMessages';
import OldRequestError from 'esm-web-pro/errors/OldRequestError';
import MeasureRoutes from 'esm-web-pro/containers/map/measure/MeasureRoutesContainer';
import GroupObjectsContainer from 'esm-web-pro/containers/map/groupobjects/GroupObjectsContainer';
import { Box } from 'grommet';
import Control from 'react-leaflet-control';
import { POINTS_MIN_ZOOM } from 'esm-web-pro/containers/pages/network/NetworkTab';
import FilterObjectsContainer from 'esm-web-pro/containers/map/filterobjects/FilterObjectsContainer';
import ZoomRestricted from '../ZoomRestricted';
import ZoomControl, { MAX_ZOOM_LEVEL } from '../controls/ZoomControl';
import type { Resource } from '../types';
import '../../../styles/map/editor.less';
import PolygonRoutesContainer from '../polygon/PolygonRoutesContainer';

type Props = {
    center: [number, number],
    zoom: number,
    resources: Array<Resource>,
    controls?: Array<{
        position: string,
        component: React.Node,
        props: Object,
        key: string,
        disabled?: boolean,
    }>,
    panes?: Array<{ // Additional Pane components https://react-leaflet.js.org/docs/en/components#pane
        name?: string,
        style?: Object,
        className?: string,
    }>,
    getData: (resources: Array<Resource>, bbox: string, queries: Object, isLastRequest: Function) => Promise<Object>,
    intl: IntlShape,
    history: History,
    bounds?: Array<[number, number]>,
    setPosition: (center: LatLng, zoom: number) => {},
    setMapLoading: (mapLoading: boolean) => {},
    confirm: (confirmation: Confirmation) => void,

    drawFeature: (
        result: Object,
        feature: Object,
        layer: Object,
        drawing: boolean,
        getPopupContent: Function,
        getFeatureTitle: Function,
    ) => void,

    updateFeature: (
        result: Object,
        feature: Object,
        layer: Object,
        drawing: boolean,
        getPopupContent: Function,
        getFeatureTitle: Function,
    ) => void,

    currentMap: Object,
    creator?: React.Node,
    allowCreate?: boolean,
    match: Object,
    location: Object,
    showMapObjects: Object,
    toggleShowMapObjects: Function,
}


type State = {
    enabledResources: { [name: string]: Resource },
    propsResources: Array<Resource>,
    errMessages: string | null,
    drawing: boolean,
    focusedLayers: Object[],
    loading: boolean,
}

export const EditorContext = React.createContext(null);

export type LoadingCallback = (layer: Object, featureGroup?: Object) => void | {
    start?: (layer: Object, featureGroup?: Object) => void,
    run: (layer: Object, featureGroup?: Object) => void,
    end?: (layer: Object, featureGroup?: Object) => void,
    cleanUp: () => void,
};

export const SRID = 4326; // Also known as WGS84, units are in degrees of longitude and latitude

export const MAP_OBJECT_KEY = 'MAP_OBJECT';

const messages = defineMessages({
    'create-save': {
        id: 'editor-create-save',
        defaultMessage: 'Save',
    },
    'update-save': {
        id: 'editor-update-save',
        defaultMessage: 'Save',
    },
    error: {
        id: 'editor-error',
        defaultMessage: 'OK',
    },
    add: {
        id: 'editor-add',
        defaultMessage: 'Add',
    },
    question: {
        id: 'editor-confirmation-question',
        defaultMessage: 'Are you sure you want to delete',
    },
    yes: {
        id: 'editor-confirmation-yes',
        defaultMessage: 'Yes',
    },
    no: {
        id: 'editor-confirmation-no',
        defaultMessage: 'No',
    },
});

export const DRAW_FG = 'drawing';
export const DRAW_PANEL_ID = 'DRAW_PANEL_ID';

const mapStyles = { height: '100%', zIndex: 0 };

let abortController = new AbortController();
let abortSignal = abortController.signal;

@withRouter
@withConfirmation
@connectAPI((apiClient: ApiClient) => ({
    getData: {
        send: (resources: Array<Resource>,
            bbox: string, queries: Object = {}, isLastRequest: Function) => {
            abortController.abort();
            return Promise.all(resources
                .map((r) => new Promise(async (resolve, reject) => {
                    const args = r.arguments || [];
                    const query = typeof r.query === 'function' ? r.query() : r.query;
                    let results = { results: { features: [] }, resource: r };
                    let next;
                    const limit = query.limit || 100;
                    let offset = 0;
                    try {
                        do {
                            if (!isLastRequest()) {
                                throw new OldRequestError('Another request is run');
                            }
                            abortController = new AbortController();
                            abortSignal = abortController.signal;
                            // eslint-disable-next-line no-await-in-loop
                            const res = await get(apiClient.resources, r.name)[r.method || 'list'](
                                ...args, {
                                    [r.bboxQueryName || 'in_bbox']: bbox,
                                    srid: SRID,
                                    ...query,
                                    ...queries[r.name],
                                    offset,
                                    limit,
                                },
                                abortSignal,
                            );
                            ({ next } = res);
                            results = {
                                ...results,
                                results: {
                                    ...res.results,
                                    features: [...results.results.features, ...res.results.features],
                                },
                            };
                            offset = res.offset + res.limit;
                        } while (next);
                    } catch (err) {
                        return reject(err);
                    }
                    return resolve(results);
                })));
        },
    },
}), true)
class BaseEditor extends React.Component<Props, State> {
    map: { current: null | Map };

    objectType: { current: null | string };

    mapPosition: { center: [number, number], zoom: number}

    _loadingCallbacks: Array<LoadingCallback> = [];

    filters: { [string]: ?Object } = {};

    featureGroups: {
        [string]: { current: null | (React.Node & { leafletElement: Object }) },
    } = {};

    currentEditedResource: ?Resource = null;

    mounted: boolean = false;

    getDataCalls: number;

    /**
     * Update enabledResources in the state when the resources passed as props are changed
     * @param {Object} props Props
     * @param {Object} state State
     * @returns {null|{enabledResources: {}, propsResources: Array<Resource>}} The changed state or nothing
     */
    static getDerivedStateFromProps(props: Props, state: State) {
        if (props.resources !== state.propsResources) {
            const newState = { enabledResources: {}, propsResources: props.resources };
            props.resources.forEach((r) => {
                newState.enabledResources[r.name] = r;
            });
            return newState;
        }

        return null;
    }

    static defaultProps = {
        // confirm: () => {},
        intl: {},
        bounds: undefined,
        creator: undefined,
        allowCreate: false,
        controls: [],
        panes: [],
    };

    /**
     * Create feature group and edit control refs for each resource. Bind onCreated callback to each resource.
     * @param {Object} props Props
     */
    constructor(props: Props) {
        super(props);
        this.featureGroups[DRAW_FG] = React.createRef();
        props.resources.forEach((r) => {
            this.featureGroups[r.name] = React.createRef();
        });

        this.getDataCalls = 0;

        this.state = {
            enabledResources: {},
            errMessages: null,
            propsResources: [],
            loading: false,
            focusedLayers: [],
            drawing: false,
            filteredResources: [],
        };

        this.map = React.createRef();
        this.objectType = React.createRef();
        this.mapPosition = {};

        this._loadFeatures = throttle(this._loadFeatures, 200, { leading: false, trailing: true });
    }

    /**
     * Wait for all feature groups to mount and load all features. Set handler to leaflet 'moveend' event so that the
     * position and center of the map are stored.
     * @returns {void}
     */
    componentDidMount() {
        this.mounted = true;

        // wait for all visible feature groups to mount
        let retries = 100;

        const checkMounted = () => {
            let mounted = null;
            if (retries > 0) {
                mounted = Object.keys(this.featureGroups).find((k) => !!this.featureGroups[k].current);
            }
            if (mounted) {
                this._loadFeatures();
            } else {
                retries -= 1;
                setTimeout(checkMounted, 100);
            }
        };

        checkMounted();
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        const { currentMap } = this.props;
        const { currentMap: previousMap } = prevProps;
        if (previousMap !== currentMap) {
            this._loadFeatures();
        }

        if (prevProps.location.pathname.includes('layer') && !this.props.location.pathname.includes('layer')) {
            this.map.current.leafletElement.invalidateSize();
        }
        if (prevProps.location.pathname !== this.props.location.pathname) {
            this.objectType.current = null;
        }
    }

    componentWillUnmount(): void {
        this.mounted = false;
    }

    registerLoadingCallback = (callback: LoadingCallback) => {
        this._loadingCallbacks.push(callback);
    };

    registerMultipleLoadingCallbacks = (callbacks: Array<LoadingCallback>) => {
        callbacks.forEach((callback) => {
            this.registerLoadingCallback(callback);
        });
    };

    unregisterLoadingCallback = (callback: LoadingCallback) => {
        const index = this._loadingCallbacks.indexOf(callback);
        if (index < 0) return;

        this._loadingCallbacks.splice(index, 1);
        if (typeof callback === 'object') {
            callback.cleanUp();
        }
    };

    unregisterMultipleLoadingCallbacks = (callbacks: Array<LoadingCallback>) => {
        callbacks.forEach((callback) => {
            this.unregisterLoadingCallback(callback);
        });
    };

    unregisterAllLoadingCallback = () => {
        this._loadingCallbacks.forEach((callback) => {
            if (typeof callback === 'object') {
                callback.cleanUp();
            }
        });
        this._loadingCallbacks = [];
    };

    handleErrorMessageClose = () => {
        this.setState({ errMessages: null });
    };

    _getFeatureTitle = (resource: Resource, feature: Object) => {
        const transformed = resource.displayFields ? resource.displayFields.map(
            (f) => feature.properties[f],
        ).filter((f) => !!f) : [];

        return transformed.join(resource.displaySeparator || ', ');
    };

    reloadFeatures = () => {
        this.startLoadingCallbacks();
        Object.values(this.featureGroups).forEach((featureGroup) => {
            if (featureGroup.current) {
                featureGroup.current.leafletElement.eachLayer((layer) => {
                    this.applyLoadingCallbacks(layer, featureGroup.current.leafletElement);
                });
            }
        });
        this.endLoadingCallbacks();
    };

    isLastRequest = (request: number) => this.getDataCalls === request;

    _loadFeatures = () => {
        if (this.map.current) {
            const {
                getData, drawFeature, confirm, intl: { formatMessage },
                updateFeature,
            } = this.props;
            const {
                enabledResources, drawing, focusedLayers,
            } = this.state;

            this.setLoading(true, () => {
                const { setMapLoading } = this.props;
                if (setMapLoading) setMapLoading(true);
            });
            this.getDataCalls += 1;
            const currentCall = this.getDataCalls;
            // eslint-disable-next-line consistent-return
            return getData(
                // $FlowFixMe
                Object.values(enabledResources).filter((r: Resource) => (
                    this.featureGroups[r.name].current && r.name !== DRAW_FG
                )),
                // $FlowFixMe
                this.map.current.leafletElement.getBounds().toBBoxString(),
                this.filters,
                () => this.isLastRequest(currentCall),
            ).then((results) => {
                if (!this.mounted || results instanceof OldRequestError || !this.isLastRequest(currentCall)
                    || results?.name === 'AbortError') {
                    return;
                }

                const layersMap = {};
                this.startLoadingCallbacks();

                results.forEach((result) => {
                    if (!result || !this.featureGroups[result.resource.name].current) {
                        return;
                    }
                    const options = {};

                    options.onEachFeature = (feature, layer) => {
                        drawFeature(result, feature, layer, drawing, this._getFeatureTitle);
                    };
                    if (result.resource.pointToLayer) {
                        options.pointToLayer = result.resource.pointToLayer;
                    }

                    const leafletFG = this.featureGroups[result.resource.name].current.leafletElement;
                    const drawingFG = this.featureGroups[DRAW_FG].current.leafletElement;
                    const resultFeatureIds = result.results.features.map((feature) => feature.id);
                    const currentFeatureIds = [];

                    let focusedLayerInEditMode;

                    focusedLayers.forEach((focusedL) => {
                        if (focusedL.pm.enabled()) {
                            focusedLayerInEditMode = focusedL;
                        }
                    });
                    leafletFG.pm.disable();

                    leafletFG.eachLayer((layer) => {
                        if (layer.feature) {
                            currentFeatureIds.push(layer.feature.id);
                            if (
                                !resultFeatureIds.includes(layer.feature.id)
                                && !focusedLayers?.includes(layer)
                            ) {
                                layer.on('remove', () => {
                                    if (result.resource.onRemove) {
                                        result.resource.onRemove(layer, this.map.current);
                                    }
                                });
                                leafletFG.removeLayer(layer);
                            } else {
                                const resultFeature = result.results.features
                                    .find((f) => f.id === layer.feature.id);
                                if (resultFeature) {
                                    layer.feature = { ...resultFeature };
                                    updateFeature(
                                        result,
                                        resultFeature,
                                        layer, drawing, this._getFeatureTitle,
                                    );
                                }
                                this.mapLayersToCoordinates(layer, layersMap);
                                this.applyLoadingCallbacks(layer, leafletFG);

                                if (result.resource.onAddToMap) {
                                    result.resource.onAddToMap(layer, this.map.current);
                                }
                            }
                        }
                    });

                    const leafLetGeoJSON = new window.L.GeoJSON(result.results.features
                        .filter((feature) => !currentFeatureIds.includes(feature.id)), options);

                    leafLetGeoJSON.eachLayer((layer) => {
                        this.mapLayersToCoordinates(layer, layersMap);
                        layer.on('add', () => {
                            if (result.resource.onAddToMap) {
                                result.resource.onAddToMap(layer, this.map.current);
                            }
                        });

                        // If the layer is already on the map, then we don't need to add it again.
                        const layerExistsInViewingFG = leafletFG.getLayers().find((leafletLayer) => (
                            leafletLayer.feature?.id === layer.feature?.id
                        ));

                        // If the layer is already on the drawing feature group, then we don't need to add it again.
                        const layerExistsInDrawingFG = drawingFG.getLayers().find((leafletLayer) => (
                            leafletLayer.feature?.id === layer.feature?.id
                        ));

                        // We only add the layer to the map if it doesn't already in any of the
                        // feature groups.
                        if (!layerExistsInViewingFG && !layerExistsInDrawingFG) {
                            leafletFG.addLayer(layer);
                        }

                        layer.on('click', (event) => {
                            // This prevents the click event from being fired for elements(layer) that is already
                            // focused by the user.
                            if (
                                !this.state[DRAW_FG]
                                && !this.map.current.leafletElement.measuring
                                && !this.map.current.leafletElement.grouping
                                && !this.state.loading
                            ) {
                                const layerIsFocused = this.state.focusedLayers?.find((focusedLayer) => (
                                    focusedLayer.feature?.id === event.target.feature?.id
                                ));
                                if (!layerIsFocused) {
                                    result.resource.navigateTo(event.target, result);
                                }
                            }
                        });
                        this.applyLoadingCallbacks(layer, leafletFG);
                    });

                    drawingFG.eachLayer((layer) => {
                        this.applyLoadingCallbacks(layer, drawingFG);
                    });

                    if (focusedLayerInEditMode) {
                        focusedLayerInEditMode.pm.enable();
                    }
                });
                this.endLoadingCallbacks();

                if (this.map.current) {
                    this.map.current.leafletElement.coordsToLayers = layersMap;
                }
                this.setLoading(false, () => {
                    const { setMapLoading } = this.props;
                    if (setMapLoading) setMapLoading(false);
                });
            }).catch((err) => {
                confirm({
                    message: <FormattedMessage
                        id="no-map-objects"
                        defaultMessage="The objects for the current map could not be loaded successfully.
                        Please refresh the page"
                    />,
                    actions: {
                        cancel: {
                            label: formatMessage(sidePanelMessages.okButton),
                            callback: () => {},
                            type: 'negative',
                        },
                    },
                });
            });
        }
        return null;
    };

    startLoadingCallbacks = () => {
        this._loadingCallbacks.forEach((loadingCallback) => {
            if (typeof loadingCallback === 'object' && loadingCallback.start) {
                loadingCallback.start();
            }
        });
    };

    applyLoadingCallbacks = (layer, leafletFG) => {
        this._loadingCallbacks.forEach((loadingCallback) => {
            if (typeof loadingCallback === 'object' && loadingCallback.run) {
                loadingCallback.run(layer, leafletFG);
            } else {
                loadingCallback(layer, leafletFG);
            }
        });
    };

    endLoadingCallbacks = () => {
        this._loadingCallbacks.forEach((loadingCallback) => {
            if (typeof loadingCallback === 'object' && loadingCallback.end) {
                loadingCallback.end();
            }
        });
    };

    mapLayersToCoordinates = (layer, layersMap) => {
        if (get(layer, 'feature.geometry.type') === 'Point' && 'toGeoJSON' in layer) {
            const point = layer.toGeoJSON().geometry.coordinates;
            layersMap[point.join('-')] = { ...layersMap[point.join('-')], point: layer };
        } else if (get(layer, 'feature.geometry.type') === 'LineString') {
            const { coordinates } = layer.toGeoJSON().geometry;
            const point1 = coordinates[0];
            const point2 = coordinates[coordinates.length - 1];

            let lines1 = layersMap[point1.join('-')] ? layersMap[point1.join('-')].lines : [];
            if (typeof lines1 === 'undefined') {
                lines1 = [];
            }

            layersMap[point1.join('-')] = {
                ...layersMap[point1.join('-')],
                lines: [
                    ...lines1,
                    layer,
                ],
            };

            let lines2 = layersMap[point2.join('-')] ? layersMap[point2.join('-')].lines : [];
            if (typeof lines2 === 'undefined') {
                lines2 = [];
            }

            layersMap[point2.join('-')] = {
                ...layersMap[point2.join('-')],
                lines: [
                    ...lines2,
                    layer,
                ],
            };
        }
        return layersMap;
    };

    handleViewportChanged = (event) => {
        const { setPosition } = this.props;
        const map = this.map.current;

        if (!isEqual(event, this.mapPosition)) {
            if (map) {
                setPosition(
                    map.leafletElement.getCenter(),
                    map.leafletElement.getZoom(),
                );
            }
            this.mapPosition = { ...event };
            this._loadFeatures();
        }
    };

    handleFiltersChange = (resource: Resource, values: Object) => {
        this.filters[resource.name] = values;
        this._loadFeatures(true);
    };

    setDrawing = (drawing, loadFeatures) => {
        this.setState({ drawing }, () => loadFeatures && this._loadFeatures());
        if (drawing && this.map.current) {
            this.map.current.leafletElement.setMinZoom(POINTS_MIN_ZOOM);
        } else if (this.map.current) {
            this.map.current.leafletElement.setMinZoom(null);
        }
    };

    focus = (layer = null) => {
        const { focusedLayers } = this.state;

        if (layer) {
            this.setState({
                focusedLayers: Array.isArray(layer) ? [...focusedLayers, ...layer] : [...focusedLayers, layer],
            });
        } else {
            this.setState({ focusedLayers: [] });
            this._loadFeatures();
        }
    };

    setLoading = (loading: boolean, callback: Function = null) => {
        this.setState({ loading }, callback);
    };

    filterResources = (resources: Object, resourcesToFilter) => {
        if (resourcesToFilter) {
            let filtered = Object.keys(resources);

            Object.values(resourcesToFilter).forEach((val) => {
                if (!val.show) {
                    filtered = filtered.filter((resource) => resource !== val.name);
                }
            });
            return filtered;
        }
        return Object.keys(resources);
    };

    render() {
        const {
            center, zoom, resources, intl: { formatMessage }, bounds, creator: CustomCreator,
            allowCreate, match: { path }, controls, panes, showMapObjects,
        } = this.props;

        const {
            enabledResources, errMessages, loading, drawing,
        } = this.state;

        return (
            <EditorContext.Provider value={this}>
                <Spinner
                    type="clipLoader"
                    loading={loading}
                    blocking={false}
                    color="black"
                    coverStyle={{
                        position: 'absolute',
                        width: 'auto',
                        height: 'auto',
                        left: '60px',
                        top: '22px',
                    }}
                />

                <Map
                    center={center}
                    zoom={zoom}
                    zoomControl={false}
                    style={mapStyles}
                    onViewportChanged={this.handleViewportChanged}
                    ref={this.map}
                    maxZoom={MAX_ZOOM_LEVEL}
                >

                    <Control position="topleft">
                        <ZoomControl />
                        {controls.map((c) => {
                            if (c.position === 'topleft') {
                                const ControlCmp = c.component;
                                return (
                                    <ControlCmp
                                        key={c.key}
                                        {...c.props}
                                        position={c.position}
                                        disabled={c.props?.argument && c.props.argument === this.objectType.current}
                                    />
                                );
                            }
                            return null;
                        })}
                    </Control>
                    {bounds && (
                        <Polygon
                            positions={bounds.map((c) => [c[1], c[0]])}
                            pmIgnore
                            color="#6d7382"
                            fill={false}
                        />
                    )}
                    <TileLayer
                        attribution={
                            '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        }
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    {panes.map((pane) => <Pane key={pane.name} {...pane} />)}
                    <Control position="topright">
                        <Box className="leaflet-control">
                            {(allowCreate && CustomCreator && this.map.current) && (
                                <DrawControl id={DRAW_PANEL_ID} />
                            )}
                            {controls.map((c) => {
                                if (c.position === 'topright') {
                                    const ControlCmp = c.component;
                                    return <ControlCmp key={c.key} {...c.props} />;
                                }
                                return null;
                            })}
                        </Box>
                    </Control>
                    <FeatureGroup ref={this.featureGroups[DRAW_FG]} key="drawing" />
                    {
                        this.filterResources(enabledResources, showMapObjects).map((key) => {
                            const r = enabledResources[key];

                            const group = (
                                // $FlowFixMe
                                <FeatureGroup ref={this.featureGroups[r.name]} key={`${r.name}-${r.key}`} />
                            );
                            if ((typeof r.minZoom !== 'undefined' || typeof r.maxZoom !== 'undefined') && r.key) {
                                return (
                                    <ZoomRestricted
                                        minZoom={r.minZoom}
                                        maxZoom={r.maxZoom}
                                        key={`${r.name}-${r.key}`}
                                    >
                                        {group}
                                    </ZoomRestricted>
                                );
                            }
                            return group;
                        })
                    }
                    <Route
                        path={`${path}/measure/`}
                        render={() => (
                            <MeasureRoutes id={DRAW_PANEL_ID} editor={this} />
                        )}
                    />
                    <Route
                        path={`${path}/polygon/`}
                        render={() => (
                            <PolygonRoutesContainer id={DRAW_PANEL_ID} editor={this} />
                        )}
                    />
                    <Route
                        path={`${path}/group/`}
                        render={() => (
                            <GroupObjectsContainer id={DRAW_PANEL_ID} editor={this} />
                        )}
                    />

                    <Route
                        path={`${path}/filter/`}
                        render={(props) => (
                            <FilterObjectsContainer id={DRAW_PANEL_ID} editor={this} {...props} />
                        )}
                    />
                    <Route
                        path={`${path}/draw/`}
                        render={() => (
                            <MapContext.Consumer>
                                {(context) => (
                                    <CreatorContainer id={DRAW_PANEL_ID}>
                                        {/* $FlowFixMe */}
                                        {(onClose) => (
                                            <CustomCreator
                                                id={DRAW_PANEL_ID}
                                                onClose={onClose}
                                                context={context}
                                                resources={resources}
                                                drawingFeatureGroup={this.featureGroups[DRAW_FG]}
                                                drawing={drawing}
                                                editor={this}
                                            />
                                        )}
                                    </CreatorContainer>
                                )}
                            </MapContext.Consumer>
                        )}
                    />
                    <Route
                        path={`${path}/layer/:objectType/:objectIdentity/`}
                        render={(props) => {
                            const { match: { params: { objectType, objectIdentity, versionType } } } = props;
                            if (objectType === 'region') {
                                this.objectType.current = 'regions';
                            } else if (objectType === 'shaft' || objectType === 'pop' || objectType === 'wallpoint'
                                || objectType === 'pillar' || objectType === 'cabinet' || objectType === 'gispoint') {
                                this.objectType.current = 'points';
                            } else if (objectType === 'gisline') {
                                this.objectType.current = 'lines';
                            }
                            return (
                                <SidePaneMapLayerContainer editor={this}>
                                    <MapContext.Consumer>
                                        {(context) => (
                                            <MapObjectSidePanel
                                                panelId={MAP_OBJECT_KEY}
                                                key={`${versionType}-${objectIdentity}-${objectType}`}
                                                map={context}
                                                resource={objectType}
                                                identity={objectIdentity}
                                                editor={this}
                                                loading={loading}
                                            />
                                        )}
                                    </MapContext.Consumer>
                                    <Route
                                        path={`${path}/layer/:objectType/:objectId/timeline/:contentType/:timelineId`}
                                        render={(timlineProps) => {
                                            const { match: { params: { timelineId } } } = timlineProps;
                                            return (
                                                <AuditEventTimeline
                                                    contentType={objectType}
                                                    objectId={timelineId}
                                                    appLabel={path.startsWith('/infrastructure') ? 'networks'
                                                        : path.split('/')[1]}
                                                />
                                            );
                                        }}
                                    />
                                </SidePaneMapLayerContainer>
                            );
                        }}
                    />
                </Map>
                {
                    errMessages ? (
                        <MessageDialog
                            message={errMessages}
                            actions={{
                                ok: {
                                    label: formatMessage(messages.error),
                                    callback: this.handleErrorMessageClose,
                                    type: 'positive',
                                },
                            }}
                            handleClose={this.handleErrorMessageClose}
                        />
                    ) : null
                }
            </EditorContext.Provider>
        );
    }
}


export default withConfirmation(injectIntl(BaseEditor, { forwardRef: true }));
